/** Дерево каталога: авторы по буквам фамилии, серии, жанры по разделам. */

import { useCallback, useEffect, useState } from 'react'
import { api } from '../api'
import type { Author, BookFilter, GenreSection, LetterCount, Series } from '../api'
import { Spinner } from './Spinner'

type Tab = 'authors' | 'series' | 'genres'

const TABS: { key: Tab; label: string }[] = [
  { key: 'authors', label: 'Авторы' },
  { key: 'series', label: 'Серии' },
  { key: 'genres', label: 'Жанры' },
]

// Сколько авторов подгружать за раз: на иную букву их десятки тысяч.
const PAGE = 300

interface Props {
  filter: BookFilter
  onSelect: (filter: BookFilter, title: string) => void
}

export function CatalogTree({ filter, onSelect }: Props) {
  const [tab, setTab] = useState<Tab>('authors')

  return (
    <div className="tree">
      <div className="tree__tabs">
        {TABS.map((item) => (
          <button
            key={item.key}
            className={`tree__tab${tab === item.key ? ' tree__tab--active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="tree__body">
        {tab === 'authors' && <AuthorBranch filter={filter} onSelect={onSelect} />}
        {tab === 'series' && <SeriesBranch filter={filter} onSelect={onSelect} />}
        {tab === 'genres' && <GenreBranch filter={filter} onSelect={onSelect} />}
      </div>
    </div>
  )
}

/** Авторы: буква → авторы этой буквы. */
function AuthorBranch({ filter, onSelect }: Props) {
  const [letters, setLetters] = useState<LetterCount[] | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [authors, setAuthors] = useState<Author[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    api.authorLetters().then((r) => setLetters(r.items)).catch(() => setLetters([]))
  }, [])

  const openLetter = useCallback(async (letter: string) => {
    if (open === letter) {
      setOpen(null)
      return
    }
    setOpen(letter)
    setAuthors([])
    setLoading(true)
    try {
      const page = await api.authors({ letter, limit: PAGE })
      setAuthors(page.items)
      setTotal(page.total)
    } finally {
      setLoading(false)
    }
  }, [open])

  const loadMore = async () => {
    setLoading(true)
    try {
      const page = await api.authors({ letter: open!, limit: PAGE, offset: authors.length })
      setAuthors((prev) => [...prev, ...page.items])
    } finally {
      setLoading(false)
    }
  }

  if (!letters) return <Spinner />

  return (
    <ul className="tree__list">
      {letters.map((item) => (
        <li key={item.letter}>
          <button
            className={`tree__node${open === item.letter ? ' tree__node--open' : ''}`}
            onClick={() => openLetter(item.letter)}
          >
            <span className="tree__caret">{open === item.letter ? '▾' : '▸'}</span>
            <span className="tree__label">{item.letter}</span>
            <span className="tree__count">{item.authors}</span>
          </button>
          {open === item.letter && (
            <ul className="tree__list tree__list--nested">
              {authors.map((author) => (
                <li key={author.id}>
                  <button
                    className={`tree__leaf${
                      filter.author_id === author.id ? ' tree__leaf--active' : ''
                    }`}
                    onClick={() => onSelect({ author_id: author.id }, author.name)}
                    title={author.name}
                  >
                    <span className="tree__label">{author.name}</span>
                    <span className="tree__count">{author.books}</span>
                  </button>
                </li>
              ))}
              {loading && <li><Spinner /></li>}
              {!loading && authors.length < total && (
                <li>
                  <button className="tree__more" onClick={loadMore}>
                    Ещё {total - authors.length}
                  </button>
                </li>
              )}
            </ul>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Серии: буква → серии этой буквы. */
function SeriesBranch({ filter, onSelect }: Props) {
  const [letters, setLetters] = useState<LetterCount[] | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [series, setSeries] = useState<Series[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    api.seriesLetters().then((r) => setLetters(r.items)).catch(() => setLetters([]))
  }, [])

  const openLetter = async (letter: string) => {
    if (open === letter) {
      setOpen(null)
      return
    }
    setOpen(letter)
    setSeries([])
    setLoading(true)
    try {
      setSeries((await api.series({ letter, limit: 2000 })).items)
    } finally {
      setLoading(false)
    }
  }

  if (!letters) return <Spinner />

  return (
    <ul className="tree__list">
      {letters.map((item) => (
        <li key={item.letter}>
          <button
            className={`tree__node${open === item.letter ? ' tree__node--open' : ''}`}
            onClick={() => openLetter(item.letter)}
          >
            <span className="tree__caret">{open === item.letter ? '▾' : '▸'}</span>
            <span className="tree__label">{item.letter}</span>
            <span className="tree__count">{item.series}</span>
          </button>
          {open === item.letter && (
            <ul className="tree__list tree__list--nested">
              {loading && <li><Spinner /></li>}
              {series.map((entry) => (
                <li key={entry.id}>
                  <button
                    className={`tree__leaf${
                      filter.series_id === entry.id ? ' tree__leaf--active' : ''
                    }`}
                    onClick={() => onSelect({ series_id: entry.id }, entry.name)}
                    title={entry.name}
                  >
                    <span className="tree__label">{entry.name}</span>
                    <span className="tree__count">{entry.books}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Жанры: раздел → жанры раздела. */
function GenreBranch({ filter, onSelect }: Props) {
  const [sections, setSections] = useState<GenreSection[] | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    api.genreTree().then((r) => setSections(r.items)).catch(() => setSections([]))
  }, [])

  if (!sections) return <Spinner />

  return (
    <ul className="tree__list">
      {sections.map((section) => (
        <li key={section.section}>
          <button
            className={`tree__node${open === section.section ? ' tree__node--open' : ''}`}
            onClick={() => setOpen(open === section.section ? null : section.section)}
          >
            <span className="tree__caret">{open === section.section ? '▾' : '▸'}</span>
            <span className="tree__label">{section.section}</span>
            <span className="tree__count">{section.books}</span>
          </button>
          {open === section.section && (
            <ul className="tree__list tree__list--nested">
              {section.genres.map((genre) => (
                <li key={genre.code}>
                  <button
                    className={`tree__leaf${
                      filter.genre === genre.code ? ' tree__leaf--active' : ''
                    }`}
                    onClick={() => onSelect({ genre: genre.code }, genre.name)}
                    title={genre.name}
                  >
                    <span className="tree__label">{genre.name}</span>
                    <span className="tree__count">{genre.books}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  )
}
